import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import DOMPurify from 'dompurify';
import MarkdownIt from 'markdown-it';
import markdownItAttrs from 'markdown-it-attrs';
import markdownItKatex from 'markdown-it-katex';
import 'katex/dist/katex.min.css';

import Spinner from '../Spinner';
import ManualBarGraphWrapper from '../graph/ManualBarGraphWrapper';
import ManualLineGraph from '../graph/ManualLineGraph';
import ManualLineBarGraph from '../graph/ManualLineBarGraph';
import ManualMarketLineGraph from '../graph/ManualMarketLineGraph';
import { GraphContainer } from '../graph/GraphContainer';
import api from '../api/api';
import ROICTreeVisualizer from '../utils/roic-tree-visualizer';

const mdParser = new MarkdownIt({
  html: true,
  breaks: true,
  linkify: true,
})
  .use(markdownItAttrs)
  .use(markdownItKatex);

const numberOutline = (text) => {
  if (!text) return '';
  const lines = text.split('\n');
  const numbering = [0, 0, 0];
  const result = [];
  lines.forEach((line) => {
    if (line === "`") {
      // 何もしない
    } else if (line.startsWith('# ')) {
      result.push(line);
    } else if (line.startsWith('## ')) {
      numbering[0]++;
      numbering[1] = 0;
      numbering[2] = 0;
      result.push(`## ${numbering[0]}. ${line.substring(3)}`);
    } else if (line.startsWith('### ')) {
      numbering[1]++;
      numbering[2] = 0;
      result.push(`### ${numbering[0]}-${numbering[1]}. ${line.substring(4)}`);
    } else if (line.startsWith('#### ')) {
      numbering[2]++;
      result.push(`#### ${numbering[0]}-${numbering[1]}-${numbering[2]}. ${line.substring(5)}`);
    } else {
      result.push(line);
    }
  });
  return result.join('\n');
};

const modifyData = (data) => {
  return data.map((dt) => {
    if (dt.x_unit === "月期" && Array.isArray(dt.x)) {
      return {
        ...dt,
        x: dt.x.map((dateStr) => {
          const date = new Date(dateStr);
          return `${date.getFullYear()}/${date.getMonth() + 1}`;
        }),
      };
    }
    return dt;
  });
};

const getFigType = (fig_data) => {
  const graphTypes = fig_data.map((fig) => fig.graphType);
  if (graphTypes.includes("bar") && graphTypes.includes("line")) {
    return "line-bar";
  } else if (graphTypes.includes("bar")) {
    return "bar";
  } else if (graphTypes.includes("line")) {
    return "line";
  } else {
    return graphTypes[0];
  }
};


const renderMarkdownWithGraphs = (text, figures, roicDataMap, modifyDataFn) => {
  if (!text) return [];

  const parts = text.split(/(@@GraphPlaceholder-\d+@@|\[roic_analysis\s+sec_codes=[^\]]+\])/g);
  const result = [];
  let placeholderBuffer = [];
  let currentGraphGroupSize = 0;
  parts.forEach((part, index) => {
    if (!part) return;
    const graphMatch = part.match(/@@GraphPlaceholder-(\d+)@@/);
    if (graphMatch) {
      const figIndex = parseInt(graphMatch[1], 10);
      const figData = figures[figIndex]?.fig_data || [];
      const figType = getFigType(figData);
      let figWidth;
      const isNextGraph = parts[index + 1] && parts[index + 1].match(/@@GraphPlaceholder-\d+@@/);
      currentGraphGroupSize = currentGraphGroupSize === 0 ? 1 : currentGraphGroupSize + 1;
      if (!isNextGraph) {
        figWidth = currentGraphGroupSize === 1 ? 500 : 400;
      }
      figWidth = "80%";
      let element = null;
      if (figType === "bar") {
        element = (
          // <ManualBarGraphWrapper
          //   key={`graph-${figIndex}`}
          //   graphId={`graph-${figIndex}`}
          //   initialData={modifyDataFn(figData)}
          //   defaultSettings={{}}
          //   downloadFilename="download.png"
          // />
          <GraphContainer 
            key={`graph-${figIndex}`}
            graphId={`graph-${figIndex}`}
            initialData={modifyDataFn(figData)}
          />
        );
      } else if (figType === "line") {
        element = (
          // <ManualLineGraph
          //   key={`graph-${figIndex}`}
          //   data={modifyDataFn(figData)}
          //   showBrush={false}
          //   showLabel="visible"
          //   colorType="default"
          //   showXaxisName={false}
          //   figWidth={figWidth}
          //   height={350}
          // />
          <GraphContainer 
            key={`graph-${figIndex}`}
            graphId={`graph-${figIndex}`}
            initialData={modifyDataFn(figData)}
          />
        );
      } else if (figType === "line-bar") {
        element = (
          <ManualLineBarGraph
            key={`graph-${figIndex}`}
            data={modifyDataFn(figData)}
            showBrush={false}
            showLabel="visible"
            colorType="default"
            showXaxisName={false}
            figWidth={figWidth}
          />
        );
      } else if (figType === "market-line") {
        element = (
          <ManualMarketLineGraph
            key={`graph-${figIndex}`}
            data={modifyDataFn(figData)}
            showBrush={false}
            colorType="default"
            showXaxisName={false}
            figWidth={figWidth}
            height={350}
          />
        );
      }
      if (element) {
        placeholderBuffer.push(element);
      }
      if (!isNextGraph) {
        currentGraphGroupSize = 0;
      }
    } else if (part && part.startsWith("[roic_analysis")) {
      const roicMatch = part.match(/\[roic_analysis\s+sec_codes=([^\]]+)\]/);
      if (roicMatch) {
        if (placeholderBuffer.length > 0) {
          result.push(
            <div key={`group-${index}`} className="flex flex-wrap gap-[1rem]">
              {placeholderBuffer}
            </div>
          );
          placeholderBuffer = [];
        }
        result.push(
          <ROICTreeVisualizer key={`roic-${index}`} companies={roicDataMap[roicMatch[0]]} />
        );
      }
    } else {
      if (part.trim() === "") return;
      if (placeholderBuffer.length > 0) {
        result.push(
          <div key={`group-${index}`} className="flex flex-wrap gap-[1rem]">
            {placeholderBuffer}
          </div>
        );
        placeholderBuffer = [];
      }
      result.push(
        <span
          key={`text-${index}`}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(mdParser.render(part)),
          }}
        />
      );
    }
  });
  if (placeholderBuffer.length > 0) {
    result.push(
      <div key={`group-end`} className="flex flex-wrap gap-[1rem]">
        {placeholderBuffer}
      </div>
    );
  }
  return result;
};

const getRoicData = async (companies) => {
  try {
    const response = await api.post(`${process.env.REACT_APP_API_URL}/chat/api/roic-data/`, { companies });
    return response.data;
  } catch (error) {
    console.error("APIエラー:", error);
    return null;
  }
};
const getGraphData = async (settings) => {
  try {
    const response = await api.post(`${process.env.REACT_APP_API_URL}/chat/api/report-graph/`, { settings });
    return response.data.fig_data;
  } catch (error) {
    console.error("APIエラー:", error);
    return null;
  }
};

/* グラフ置換の memo 化版 */
const replaceGraphMemoized = async (contents, setProcessedContent, renderMarkdownWithGraphsFn, modifyDataFn, graphMemoRef) => {
  let updatedText = contents;
  const newFigures = [];
  const regex = /\[type=(.+?)\]/g;
  let match;
  let index = 0;
  while ((match = regex.exec(contents)) !== null) {
    const settings = match[0];
    let cachedGraph = graphMemoRef.current[settings];
    if (!cachedGraph) {
      const graphData = await getGraphData(settings);
      cachedGraph = {
        fig_data: graphData,
        replace_string: settings,
      };
      graphMemoRef.current[settings] = cachedGraph;
    }
    newFigures.push(cachedGraph);
    updatedText = updatedText.replace(settings, `@@GraphPlaceholder-${index}@@`);
    index++;
  }
  // ROICグラフ処理
  const roicRegex = /\[roic_analysis\s+sec_codes=([^\]]+)\]/g;
  const roicPlaceholders = [];
  let roicMatch;
  while ((roicMatch = roicRegex.exec(contents)) !== null) {
    const settingString = roicMatch[0];
    const secCodes = roicMatch[1].split(",").map(code => code.trim());
    roicPlaceholders.push({ settingString, secCodes });
  }
  const roicDataMap = {};
  for (const item of roicPlaceholders) {
    const data = await getRoicData(item.secCodes);
    roicDataMap[item.settingString] = data;
  }
  setProcessedContent(renderMarkdownWithGraphsFn(updatedText, newFigures, roicDataMap, modifyDataFn));
};

/* 参照記法の置換 */
const replaceRef = (contents, setReferences) => {
  if (!contents) return;
  const refRegex = /\[ref (.*?)\]/g;
  let matches = [];
  let match;
  while ((match = refRegex.exec(contents)) !== null) {
    matches.push(match[1]);
  }
  const uniqueRefs = [...new Set(matches)];
  const refMap = new Map();
  uniqueRefs.forEach((ref, index) => refMap.set(ref, index + 1));
  let updatedContents = contents.replace(refRegex, (match, p1) => {
    return `<a class="text-xs" href="#ref-${refMap.get(p1)}">[${refMap.get(p1)}]</a>`;
  });
  setReferences(uniqueRefs);
  return updatedContents;
};

/* ---------------------------
   CommonDocument コンポーネント
   ・差分更新時、新規追記部分に [type=...] タグが含まれている場合、
     タグが正しく閉じているときのみグラフ置換を実施し、prevContents に反映
     （閉じていなければ、まだ不完全とみなし prevContents の更新は行わない）
------------------------------ */
const CommonDocument = ({
  contents,
  figs,
  showProcessing,
  replaceGraphFlg = false,
  modifyDataFn = modifyData,
}) => {
  const [processedContent, setProcessedContent] = useState(null);
  const [references, setReferences] = useState([]);
  const [prevContents, setPrevContents] = useState('');
  const [prevParsedContext, setPrevParsedContext] = useState('');
  const graphMemoRef = useRef({});
  const [width, setWidth] = useState(0); // 幅を管理する状態
  const ref = useRef(null); 


  const generateGraphId = () => `document_graph`;
      
  // 現在のグラフIDを生成（メモ化）
  const currentGraphId = useMemo(
      () => generateGraphId(),
      []
  );

  // すべてのグラフに設定を適用する処理
  const handleApplySettingsToAll = useCallback((newSettings) => {
  }, []);

  // containerのサイズをグラフに渡すため 
  useEffect(() => {
  if (!ref.current) return;

  // ResizeObserverを定義
  const observer = new ResizeObserver((entries) => {
  for (let entry of entries) {
      if (entry.contentRect) {
          setWidth(entry.contentRect.width); // ref要素の幅を設定
      }
  }
      });

      // ref要素を監視
      observer.observe(ref.current);

      // クリーンアップ処理
      return () => {
      if (ref.current) {
          observer.unobserve(ref.current);
      }
      };
  }, []);


  useEffect(() => {
    if (!contents) {
      setProcessedContent(null);
      setPrevContents('');
      setPrevParsedContext('');
      return;
    }
    let finalParsed;
    // 前回の内容と比較して、完全な更新か差分かを判定
    if (!prevContents || !contents.startsWith(prevContents)) {
      // 前回との差分でなく、全体が変わっている場合はフルパース
      finalParsed = numberOutline(contents);
      setPrevContents(contents);
      setPrevParsedContext(finalParsed);
    } else {
      // 追記分のみ
      const newPart = contents.slice(prevContents.length);
      // もし追記部分に [type= があれば、閉じる "]" があるかをチェック
      const hasGraphTag = /\[type=/.test(newPart);
      const completeGraphTag = /\[type=[^\]]+\]/.test(newPart);
      if (hasGraphTag && !completeGraphTag) {
        // タグが開始しているが閉じていないので、安定部分に含めず、更新のみ反映する（prevContentsはそのまま）
        finalParsed = prevParsedContext + newPart;
      } else {
        // [type=...] タグが完全なら、再パースして安定部分に含める
        if (replaceGraphFlg) {
          finalParsed = prevParsedContext + numberOutline(newPart);
        } else {
          finalParsed = prevParsedContext + newPart;
        }
        
        // 完全な新部分なら prevContents も更新
        setPrevContents(contents);
        setPrevParsedContext(finalParsed);
      }
    }
    const html = mdParser.render(finalParsed);
    setProcessedContent(
      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />
    );
  }, [contents, prevContents, prevParsedContext]);

  // replaceGraphFlgがtrue の場合にグラフ置換処理を実施
  useEffect(() => {
    if (replaceGraphFlg && processedContent) {
      const contentStr =
        typeof processedContent === 'string'
          ? processedContent
          : processedContent.props?.dangerouslySetInnerHTML.__html || '';
      const updatedContents = replaceRef(contentStr, setReferences);
      (async () => {
        await replaceGraphMemoized(
          updatedContents,
          (newProcessedContent) => {
            // 必要に応じて、新しいコンテンツと既存のものを比較して更新
            if (
              !processedContent ||
              processedContent.props?.dangerouslySetInnerHTML.__html !==
                newProcessedContent.props?.dangerouslySetInnerHTML.__html
            ) {
              setProcessedContent(newProcessedContent);
            }
          },
          renderMarkdownWithGraphs,
          modifyDataFn,
          graphMemoRef
        );
      })();
    }
  }, [processedContent, replaceGraphFlg, modifyDataFn]);

  const ReferenceArea = React.memo(() => (
    <>
      <hr />
      <div className="mt-4">
        <h2 className="text-lg font-bold">参照</h2>
        <div className="my-1 py-2">
          {references.map((ref, index) => (
            <div key={index} id={`ref-${index + 1}`}>
              [{index + 1}] {ref}
            </div>
          ))}
        </div>
      </div>
    </>
  ));

  return (
    <div className="document-draft h-full overflow-y-auto">
      {showProcessing && <Spinner />}
      <div contentEditable={false} className="p-3 rounded-md min-h-[100px] transition-all">
        {processedContent}
        {references && references.length > 0 && <ReferenceArea />}
      </div>
    </div>
  );
};

export default CommonDocument;
